import React, { Component } from "react";
import { observable, reaction, toJS } from "mobx";
import { inject, observer } from "mobx-react";
import { Button, Popover, Position, Spinner } from "@blueprintjs/core";
import { DatePicker } from "@blueprintjs/datetime";
import moment from "moment";
import _ from "lodash";
import { Helmet } from "react-helmet";
import "mapbox-gl/dist/mapbox-gl.css";
import { router } from "../App";
import { channels } from "../services/pusher";
import ServiceTypePicker from "components/ServiceTypePicker";
import TechnicianPicker from "components/TechnicianPicker";
import PriorityTagPicker from "components/PriorityTagPicker";
import DispatchViewBar from "components/DispatchViewBar";
import SearchFiltersToolbar from "components/SearchFiltersToolbar";
import { priorityMapping } from "helpers/priorityTag";

const mapboxgl = require("mapbox-gl/dist/mapbox-gl.js");

const UNASSIGNED_COLOR = "#9aa5b5";
// Same grace period as the timeline's hover cards, so the pointer can move
// from a marker or list card onto its popup.
const POPUP_HIDE_DELAY = 300;
// Real-time updates often arrive in bursts (one per assignment).
const REFRESH_DEBOUNCE = 500;

const directionsUrl = (job) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${job.latitude},${job.longitude}`
  )}`;

// One entry per job (a job has one assignment per technician), for the
// assignments that overlap the selected day.
const jobsForDay = (assignments, date) => {
  const dayStart = moment.unix(Number(date)).startOf("day");
  const dayEnd = moment.unix(Number(date)).endOf("day");
  const onDay = assignments.filter(
    ({ attributes }) =>
      moment(attributes.start_date).isBefore(dayEnd) &&
      moment(attributes.end_date).isAfter(dayStart)
  );

  return _.chain(onDay)
    .groupBy(({ attributes }) => attributes.job_id || attributes.id)
    .map((group) => {
      const { attributes } = group[0];
      const location = attributes.location || {};
      const job = attributes.job || {};
      return {
        id: attributes.job_id || attributes.id,
        eventNumber: attributes.event_number,
        title: attributes.title,
        clientName: attributes.client && attributes.client.name,
        address: location.full_address,
        latitude: location.latitude,
        longitude: location.longitude,
        start: attributes.start_date,
        end: attributes.end_date,
        status: job.status || attributes.status,
        priority: (attributes.priority || job.priority || "").toLowerCase(),
        isRecurring: !!(
          attributes.service_agreement && attributes.service_agreement.id
        ),
        color: attributes.assignment_color || UNASSIGNED_COLOR,
        serviceType: attributes.service_type || {},
        technicians: _.uniq(
          group.map(({ attributes: a }) => a.technician_name).filter(Boolean)
        ),
      };
    })
    .sortBy((job) => moment(job.start).valueOf())
    .value();
};

const spotKey = (job) =>
  `${Number(job.latitude).toFixed(5)},${Number(job.longitude).toFixed(5)}`;

const hasLocation = (job) =>
  job.latitude !== null &&
  job.longitude !== null &&
  _.isFinite(Number(job.latitude)) &&
  _.isFinite(Number(job.longitude)) &&
  !(Number(job.latitude) === 0 && Number(job.longitude) === 0);

// "Oct 9, 26 10:15am - 12:45pm", as on the timeline cards.
const dateRange = (job) => {
  const start = moment(job.start);
  const end = moment(job.end);
  return start.isSame(end, "day")
    ? `${start.format("MMM D, YY h:mma")} - ${end.format("h:mma")}`
    : `${start.format("MMM D, YY h:mma")} - ${end.format("MMM D, YY h:mma")}`;
};

const duration = (job) => {
  const minutes = moment(job.end).diff(moment(job.start), "minutes");
  return [
    [Math.floor(minutes / 1440), "d"],
    [Math.floor((minutes % 1440) / 60), "h"],
    [minutes % 60, "m"],
  ]
    .filter(([amount]) => amount > 0)
    .map(([amount, unit]) => `${amount}${unit}`)
    .join(" ");
};

const technicianNames = (job) => job.technicians.join(", ") || "Unassigned";

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};

const PriorityTag = ({ priority, className = "" }) => {
  if (!priority) return null;
  const mapping = priorityMapping(priority);
  return (
    <span
      className={`priority-tag ${className}`}
      style={{
        backgroundColor: mapping.background,
        color: mapping.text,
        borderColor: mapping.text,
      }}
    >
      {mapping.emoji} {mapping.label}
    </span>
  );
};

// List card laid out like the job cards on the timeline.
const JobCard = ({ job, selected, onMap, onClick, onMouseEnter, onMouseLeave }) => (
  <button
    type="button"
    className={`dispatch-map__job${selected ? " is-selected" : ""}${onMap ? "" : " is-unlocated"}`}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    title={onMap ? undefined : "No map location"}
  >
    <span
      className="dispatch-map__job-bar"
      style={{ backgroundColor: job.color }}
    />
    <span className="dispatch-map__job-row">
      <span className="dispatch-map__job-number">
        #{job.eventNumber}
        {job.isRecurring && (
          <span className="bp3-icon-standard bp3-icon-repeat" />
        )}
        {job.status === "invoiced" && (
          <span className="bp3-icon-standard bp3-icon-tick-circle bp3-intent-success" />
        )}
      </span>
      {job.serviceType.name && (
        <span
          className="dispatch-map__job-tag"
          style={
            job.serviceType.color
              ? { backgroundColor: `#${job.serviceType.color}` }
              : undefined
          }
        >
          {job.serviceType.name}
        </span>
      )}
    </span>
    <span className="dispatch-map__job-title">{job.title}</span>
    <span className="dispatch-map__job-row">
      <span className="dispatch-map__job-client">{job.clientName}</span>
      <PriorityTag priority={job.priority} />
    </span>
    {job.address && (
      <span className="dispatch-map__job-address">{job.address}</span>
    )}
    <span className="dispatch-map__job-technician">{technicianNames(job)}</span>
    <span className="dispatch-map__job-date">{dateRange(job)}</span>
  </button>
);

// Dispatch board jobs for one day on a map, with the board's filters.
@inject("viewStore", "dashboard", "jobStore")
@observer
class DispatchMap extends Component {
  @observable jobs = [];
  @observable isLoading = false;
  // Spot whose popup is open, highlighted in the list.
  @observable activeSpot = null;

  markers = [];
  popup = null;
  popupPinned = false;
  hideTimer = null;

  componentDidMount() {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
    this.map = new mapboxgl.Map({
      container: this.mapContainer,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [-79.3832, 43.6532],
      zoom: 9,
    });
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right");
    // The container gets its final size after the stylesheet and layout settle.
    this.resizeObserver = new ResizeObserver(() => this.map.resize());
    this.resizeObserver.observe(this.mapContainer);
    this.load({ fit: true });

    // Refresh in place when a job is saved from the edit drawer, or changed
    // elsewhere (the board's real-time channel).
    this.disposeRefresh = reaction(
      () => this.props.jobStore.dispatchChangedAt,
      () => this.refresh()
    );
    this.channel = channels.dispatchBoard();
    this.channel.bind("assignment-state-change", this.refreshSoon);
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.cancelHide();
    clearTimeout(this.refreshTimer);
    if (this.disposeRefresh) this.disposeRefresh();
    if (this.channel) {
      this.channel.unbind("assignment-state-change", this.refreshSoon);
    }
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.map) this.map.remove();
  }

  get search() {
    return this.props.viewStore.dashboardSearch;
  }

  // Reload without moving the map.
  refresh = () => this.load({ fit: false });

  refreshSoon = () => {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(this.refresh, REFRESH_DEBOUNCE);
  };

  load = ({ fit }) => {
    const day = moment.unix(Number(this.search.date));
    this.isLoading = true;
    const request = (this.request = this.props.dashboard.loadAssignments(
      day.clone().startOf("day"),
      day.clone().endOf("day")
    ));
    request
      .then((response) => {
        if (this.unmounted || request !== this.request) return;
        this.jobs = jobsForDay(response.data.data || [], this.search.date);
        this.renderMarkers({ fit });
      })
      .catch(() => {
        if (request === this.request) this.jobs = [];
      })
      .finally(() => {
        if (request === this.request) this.isLoading = false;
      });
  };

  // Jobs at the same spot share one marker, showing how many there are.
  get spots() {
    return _.chain(this.jobs.filter(hasLocation))
      .groupBy(spotKey)
      .map((jobs, key) => ({
        key,
        jobs,
        lngLat: [Number(jobs[0].longitude), Number(jobs[0].latitude)],
      }))
      .value();
  }

  renderMarkers({ fit }) {
    // Markers are DOM elements, so they don't wait for the map's tiles.
    if (!this.map) return;
    const openSpot = this.popup && this.activeSpot;
    const wasPinned = this.popupPinned;
    this.markers.forEach(({ marker }) => marker.remove());
    this.closePopup();

    this.markers = this.spots.map((spot) => {
      const node = el("button", "dispatch-map-marker");
      node.type = "button";
      node.style.setProperty("--marker-color", spot.jobs[0].color);
      node.setAttribute(
        "aria-label",
        spot.jobs.map((job) => `#${job.eventNumber} ${job.title}`).join(", ")
      );
      // Mapbox positions the marker with an inline transform, so the pin
      // shape (rotated) lives on an inner element.
      const pin = el("span", "dispatch-map-marker__pin");
      pin.appendChild(
        el(
          "span",
          "dispatch-map-marker__label",
          spot.jobs.length > 1 ? String(spot.jobs.length) : ""
        )
      );
      node.appendChild(pin);
      node.addEventListener("mouseenter", () => this.openPopup(spot));
      node.addEventListener("mouseleave", this.scheduleHide);
      node.addEventListener("click", (e) => {
        e.stopPropagation();
        this.selectSpot(spot);
      });
      const marker = new mapboxgl.Marker({ element: node, anchor: "bottom" })
        .setLngLat(spot.lngLat)
        .addTo(this.map);
      return { spot, marker };
    });

    if (fit) {
      this.fitToMarkers({ animate: false });
    } else if (openSpot) {
      // Refreshed in place: reopen the popup that was showing, with new data.
      const entry = this.markers.find(({ spot }) => spot.key === openSpot);
      if (entry) this.openPopup(entry.spot, { pinned: wasPinned });
    }
  }

  fitToMarkers = ({ animate = true } = {}) => {
    if (!this.map || this.markers.length === 0) return;
    if (this.markers.length === 1) {
      this.map.jumpTo({ center: this.markers[0].spot.lngLat, zoom: 14 });
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    this.markers.forEach(({ spot }) => bounds.extend(spot.lngLat));
    this.map.fitBounds(bounds, { padding: 80, maxZoom: 14, animate });
  };

  spotForJob(job) {
    const entry = this.markers.find(({ spot }) => spot.jobs.includes(job));
    return entry && entry.spot;
  }

  // Hovering opens a spot's popup; it closes shortly after the pointer
  // leaves, unless it was pinned by a click.
  openPopup(spot, { pinned = false } = {}) {
    this.cancelHide();
    if (this.popup && this.activeSpot === spot.key) {
      if (pinned) this.popupPinned = true;
      return;
    }
    this.closePopup();

    const popup = new mapboxgl.Popup({
      // The pin is ~38px tall above its point, so lift popups shown above it.
      offset: {
        bottom: [0, -40],
        "bottom-left": [0, -40],
        "bottom-right": [0, -40],
        top: [0, 6],
        "top-left": [0, 6],
        "top-right": [0, 6],
        left: [16, -20],
        right: [-16, -20],
      },
      maxWidth: "300px",
      className: "dispatch-map-popup",
      focusAfterOpen: false,
    })
      .setLngLat(spot.lngLat)
      .setDOMContent(this.popupContent(spot.jobs))
      .addTo(this.map);

    const node = popup.getElement();
    node.addEventListener("mouseenter", this.cancelHide);
    node.addEventListener("mouseleave", this.scheduleHide);
    popup.on("close", () => {
      if (this.popup !== popup) return;
      this.popup = null;
      this.activeSpot = null;
      this.popupPinned = false;
    });

    this.popup = popup;
    this.activeSpot = spot.key;
    this.popupPinned = pinned;
  }

  closePopup() {
    this.cancelHide();
    if (this.popup) this.popup.remove();
  }

  cancelHide = () => {
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
  };

  scheduleHide = () => {
    if (this.popupPinned) return;
    this.cancelHide();
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (!this.popupPinned) this.closePopup();
    }, POPUP_HIDE_DELAY);
  };

  // Clicking zooms in on the spot and keeps its popup open.
  selectSpot = (spot) => {
    this.openPopup(spot, { pinned: true });
    this.map.flyTo({
      center: spot.lngLat,
      zoom: Math.max(this.map.getZoom(), 15),
      // Leave room above the pin for the popup.
      offset: [0, 120],
    });
  };

  onJobClick = (job) => {
    const spot = this.spotForJob(job);
    if (spot) this.selectSpot(spot);
  };

  // Hovering a list card shows its popup, bringing the marker into view.
  onJobEnter = (job) => {
    const spot = this.spotForJob(job);
    if (!spot) return;
    if (!this.map.getBounds().contains(spot.lngLat)) {
      this.map.easeTo({ center: spot.lngLat, offset: [0, 120] });
    }
    this.openPopup(spot);
  };

  // Same layout and classes as the timeline's hover card.
  popupContent(jobs) {
    const root = el("div", "dispatch-map-popup__jobs");
    jobs.forEach((job) => {
      const card = el("div", "card-hover-block");

      const top = el("div", "card-top");
      top.appendChild(el("div", "event-number", `#${job.eventNumber}`));
      if (job.serviceType.name) {
        const tag = el("div", "event-tag", job.serviceType.name);
        if (job.serviceType.color) {
          tag.style.backgroundColor = `#${job.serviceType.color}`;
        }
        top.appendChild(tag);
      }
      card.appendChild(top);
      card.appendChild(el("div", "h-client-title", job.title));

      const badges = el("div", "h-badges");
      if (job.status) {
        const status = el("span", "h-status");
        const dot = el("span", "h-status-dot");
        dot.style.backgroundColor = job.color;
        status.appendChild(dot);
        status.appendChild(document.createTextNode(_.startCase(job.status)));
        badges.appendChild(status);
      }
      if (job.priority) {
        const mapping = priorityMapping(job.priority);
        const tag = el(
          "span",
          "priority-tag",
          `${mapping.emoji} ${mapping.label}`
        );
        tag.style.backgroundColor = mapping.background;
        tag.style.color = mapping.text;
        tag.style.borderColor = mapping.text;
        badges.appendChild(tag);
      }
      if (badges.childNodes.length) card.appendChild(badges);

      const list = el("ul", "list-unstyled");
      list.appendChild(el("li", "h-client icon-marker", job.clientName));
      if (job.address) {
        list.appendChild(el("li", "h-client-address", job.address));
      }
      list.appendChild(el("li", "h-client icon-user", technicianNames(job)));
      const date = el("li", "h-card-date icon-calender", dateRange(job));
      if (duration(job)) {
        date.appendChild(el("span", "h-duration", ` · ${duration(job)}`));
      }
      list.appendChild(date);
      if (job.isRecurring) {
        const recurring = el("li", "h-recurring", "Part of a service agreement");
        recurring.prepend(el("span", "bp3-icon-standard bp3-icon-repeat"));
        list.appendChild(recurring);
      }
      card.appendChild(list);

      const actions = el("div", "add-task-button");
      const edit = el("button", "card-edit-button");
      edit.type = "button";
      edit.appendChild(el("span", "bp3-icon-standard bp3-icon-edit"));
      edit.appendChild(document.createTextNode(" Edit"));
      edit.addEventListener("click", () =>
        window.stores.jobStore.openEditJobDrawer(job.id)
      );
      const directions = el("button", "card-directions-button");
      directions.type = "button";
      directions.appendChild(
        el("span", "bp3-icon-standard bp3-icon-direction-right")
      );
      directions.appendChild(document.createTextNode(" Directions"));
      directions.addEventListener("click", () =>
        window.open(directionsUrl(job), "_blank", "noopener")
      );
      const more = el("button", "card-more-button", "More info");
      more.type = "button";
      more.addEventListener("click", () =>
        router.navigate(`/dashboard/jobs/${job.id}`)
      );
      actions.appendChild(edit);
      actions.appendChild(directions);
      actions.appendChild(more);
      card.appendChild(actions);

      root.appendChild(card);
    });
    return root;
  }

  updateSearch(changes) {
    const { viewStore } = this.props;
    viewStore.dashboardSearch = { ...this.search, ...changes };
    viewStore.dashboardMap();
    this.load({ fit: true });
  }

  goToDate = (date) => {
    if (date && Number(this.search.date) !== moment(date).unix()) {
      this.updateSearch({ date: moment(date).unix().toString() });
    }
  };

  render() {
    const { date, service_type_id, technician_id, priority } = this.search;
    const located = this.jobs.filter(hasLocation);
    const missing = this.jobs.length - located.length;

    return (
      <div className="responsive-layout-navbar dispatch-board dispatch-map-page">
        <Helmet>
          <title>Dispatch Map | Field Nexus</title>
        </Helmet>

        <div className="dispatch-board-header">
          <SearchFiltersToolbar
            className="filter-bar--stretch"
            header={
              <DispatchViewBar
                current="map"
                status={this.isLoading && <Spinner size={16} />}
              />
            }
          >
            <Popover
              content={
                <DatePicker
                  className="dispatch-date-picker"
                  defaultValue={moment.unix(Number(date)).toDate()}
                  onChange={this.goToDate}
                  showActionsBar={true}
                  maxDate={moment().add(500, "years").toDate()}
                  minDate={moment().subtract(500, "years").toDate()}
                />
              }
              position={Position.BOTTOM_LEFT}
              usePortal={true}
            >
              <Button
                className="filter-date"
                icon="calendar"
                rightIcon="caret-down"
                text={moment.unix(Number(date)).format("ddd, MMM D, YYYY")}
              />
            </Popover>
            <ServiceTypePicker
              selectedOptions={service_type_id || []}
              maximumSelections={10}
              onChange={(selections) =>
                this.updateSearch({
                  service_type_id: selections.map((s) => s.value),
                })
              }
              includeSelectAll={true}
            />
            <TechnicianPicker
              selectedOptions={technician_id || []}
              onChange={(technicians) =>
                this.updateSearch({
                  technician_id: toJS(technicians.map((t) => t.value)),
                })
              }
              includeAdmins={true}
              includeUnassigned={true}
              includeSelectAll={true}
            />
            <PriorityTagPicker
              selectedOptions={toJS(priority) || []}
              onChange={(priorities) =>
                this.updateSearch({
                  priority: toJS(priorities.map((p) => p.value)),
                })
              }
              includeSelectAll={true}
            />
          </SearchFiltersToolbar>
        </div>

        <div className="dispatch-map">
          <aside className="dispatch-map__list" aria-label="Jobs on this day">
            <div className="dispatch-map__list-header">
              <strong>
                {this.jobs.length} {this.jobs.length === 1 ? "job" : "jobs"}
              </strong>
              {located.length > 0 && (
                <Button
                  minimal={true}
                  small={true}
                  icon="zoom-to-fit"
                  text="Fit all"
                  onClick={() => this.fitToMarkers()}
                />
              )}
            </div>
            {missing > 0 && (
              <div className="dispatch-map__notice">
                {missing} {missing === 1 ? "job has" : "jobs have"} no map
                location
              </div>
            )}
            {!this.isLoading && this.jobs.length === 0 && (
              <div className="dispatch-map__empty">No jobs on this day</div>
            )}
            {this.jobs.map((job) => {
              const onMap = hasLocation(job);
              return (
                <JobCard
                  key={job.id}
                  job={job}
                  onMap={onMap}
                  selected={onMap && this.activeSpot === spotKey(job)}
                  onClick={() => onMap && this.onJobClick(job)}
                  onMouseEnter={() => onMap && this.onJobEnter(job)}
                  onMouseLeave={this.scheduleHide}
                />
              );
            })}
          </aside>
          <div
            className="dispatch-map__canvas"
            ref={(node) => {
              this.mapContainer = node;
            }}
          />
        </div>
      </div>
    );
  }
}

export default DispatchMap;
